import React, { useState, useEffect } from 'react';
import { TextField, Box, makeStyles } from '@material-ui/core';
import moment from 'moment-timezone';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-start',
    width: '100%',
  },
  dateBox: {
    flex: '1 1 210px',
    minWidth: 210,
  },
  timeBox: {
    flex: '0 0 200px',
    minWidth: 200,
    maxWidth: 240,
  },
  timeField: {
    '& .MuiOutlinedInput-root': {
      overflow: 'visible',
    },
    '& input[type="time"]': {
      minWidth: 148,
      fontVariantNumeric: 'tabular-nums',
    },
    '& input[type="time"]::-webkit-datetime-edit': {
      minWidth: '8.5ch',
      padding: '0 2px',
    },
    '& input[type="time"]::-webkit-calendar-picker-indicator': {
      marginLeft: 6,
      flexShrink: 0,
    },
  },
}));

/**
 * ✅ COMPONENTE: DateTimePicker SIMPLE con Timezone
 * 
 * Trabaja SOLO con strings para evitar conversiones automáticas de Date.
 * Dos campos separados: uno para fecha (date) y otro para hora (time).
 * 
 * @param {string} value - Fecha en formato "YYYY-MM-DD HH:mm:ss" (zona empresa)
 * @param {function} onChange - Callback que recibe string "YYYY-MM-DD HH:mm:ss"
 * @param {string} timezone - Zona horaria IANA (ej: "America/Lima")
 */
export default function DateTimePickerWithTimezone({
  value,
  onChange,
  onBlur,
  timezone,
  label,
  error,
  helperText,
  disabled,
  minDate,
  ...props
}) {
  const classes = useStyles();
  const [dateValue, setDateValue] = useState('');
  const [timeValue, setTimeValue] = useState('');
  const [lastValue, setLastValue] = useState('');

  // ✅ Sincronizar con valor externo SOLO si realmente cambió
  useEffect(() => {
    // ✅ CORRECCIÓN: Solo actualizar si el valor realmente cambió (comparación de strings)
    // Esto evita actualizaciones innecesarias cuando el componente se re-renderiza
    const valueStr = value ? String(value) : '';
    if (valueStr === lastValue) {
      return; // No hacer nada si el valor no cambió
    }
    
    setLastValue(valueStr); // Guardar el nuevo valor
    
    if (!value || value === '') {
      setDateValue('');
      setTimeValue('');
      return;
    }

    try {
      // Parsear "YYYY-MM-DD HH:mm:ss" directamente
      const parts = String(value).split(' ');
      if (parts.length === 2) {
        const parsedDate = parts[0]; // YYYY-MM-DD
        const parsedTime = parts[1].substring(0, 5); // HH:mm (sin segundos)
        
        setDateValue(parsedDate);
        setTimeValue(parsedTime);
      }
    } catch (error) {
      console.error('[DateTimePickerWithTimezone] Error parseando valor:', error);
    }
  }, [value, lastValue]);

  // ✅ Handler de cambio
  const handleChange = (newDate, newTime) => {
    // ✅ CORRECCIÓN: Validar que ambos valores estén presentes y no sean vacíos
    if (!newDate || !newTime || newDate === '' || newTime === '') {
      console.log(`🕐 [DateTimePickerWithTimezone] Valores incompletos - newDate: "${newDate}", newTime: "${newTime}" - No devolviendo valor`);
      if (onChange) onChange(null);
      return;
    }

    // Combinar fecha + hora directamente como string
    const combined = `${newDate} ${newTime}:00`;
    
    // Validar que sea una fecha válida en la zona empresa
    const momentInTz = moment.tz(combined, 'YYYY-MM-DD HH:mm:ss', timezone);
    
    if (!momentInTz.isValid()) {
      console.warn(`⚠️ [DateTimePickerWithTimezone] Fecha/hora inválida: "${combined}" (timezone: ${timezone})`);
      return;
    }

    // ✅ Validar valores inválidos (solo 1970-01-01 que es fecha por defecto inválida)
    if (combined === '1970-01-01 00:00:00') {
      return;
    }

    // Devolver el string directamente
    if (onChange) {
      onChange(combined);
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDateValue(newDate);
    handleChange(newDate, timeValue);
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setTimeValue(newTime);
    handleChange(dateValue, newTime);
  };

  // ✅ Handler para onBlur - se ejecuta cuando cualquiera de los campos pierde el foco
  const handleBlur = () => {
    if (onBlur && value) {
      onBlur();
    }
  };

  // ✅ Formatear nombre de zona horaria
  const getTimezoneDisplayName = () => {
    if (!timezone) return '';
    const parts = timezone.split('/');
    return parts[parts.length - 1].replace(/_/g, ' ');
  };

  const finalHelperText = error ? helperText : `🌍 Hora en ${getTimezoneDisplayName()}`;

  // ✅ Calcular minDate para el input (formato YYYY-MM-DD)
  const getMinDateString = () => {
    if (!minDate) return undefined;
    if (minDate instanceof Date) {
      const year = minDate.getFullYear();
      const month = String(minDate.getMonth() + 1).padStart(2, '0');
      const day = String(minDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return undefined;
  };

  return (
    <Box className={classes.row}>
      <Box className={classes.dateBox}>
        <TextField
          label={label}
          type="date"
          value={dateValue}
          onChange={handleDateChange}
          onBlur={handleBlur}
          error={Boolean(error)}
          helperText={finalHelperText}
          disabled={disabled}
          inputProps={{ min: getMinDateString() }}
          variant="outlined"
          margin="dense"
          fullWidth
          InputLabelProps={{ shrink: true }}
          {...props}
        />
      </Box>
      <Box className={classes.timeBox}>
        <TextField
          label="Hora"
          type="time"
          value={timeValue}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          error={Boolean(error)}
          disabled={disabled}
          variant="outlined"
          margin="dense"
          fullWidth
          className={classes.timeField}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
        />
      </Box>
    </Box>
  );
}
